<template>
  <Card
    v-if="liveMeeting"
    role="button"
    tabindex="0"
    :aria-label="`${meetingUsher.actionTitle(liveMeeting)}: ${meetingUsher.eventTypeLabel(liveMeeting)} happening now`"
    class="group/card cursor-pointer border-l-4 border-l-green-500 py-0 transition-colors hover:bg-green-50/60 focus-visible:bg-green-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500/40 animate-in fade-in-0 slide-in-from-top-2 duration-500"
    @click="meetingUsher.navigateToMeeting(liveMeeting)"
    @keydown.enter.prevent="meetingUsher.navigateToMeeting(liveMeeting)"
    @keydown.space.prevent="meetingUsher.navigateToMeeting(liveMeeting)"
  >
    <CardContent class="flex flex-col gap-1 px-4 py-2.5">
      <div class="flex items-center gap-2.5">
        <span class="relative flex h-2 w-2 shrink-0" aria-hidden="true">
          <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75"></span>
          <span class="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
        </span>
        <span class="text-sm font-medium text-gray-900">
          {{ meetingUsher.eventTypeLabel(liveMeeting) }} happening now
        </span>
        <Badge variant="green" :capitalize="false">Live</Badge>
      </div>

      <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-[18px] text-xs text-muted-foreground">
        <span class="tabular-nums">
          {{ formatDate(liveMeeting.start_time, 'h:mm a') }} – {{ formatDate(liveMeeting.end_time, 'h:mm a') }}
        </span>
        <span v-if="locationInfo" class="flex min-w-0 items-center gap-1">
          <vue-feather :type="locationInfo.icon" class="!h-3 !w-3 shrink-0" />
          <span class="truncate">{{ locationInfo.label }}</span>
        </span>
      </div>

      <div class="flex items-center gap-1 pl-[18px] pt-0.5 text-xs font-medium text-indigo-600">
        <span>{{ meetingUsher.actionTitle(liveMeeting) }}</span>
        <vue-feather
          type="arrow-right"
          class="!h-3 !w-3 transition-transform duration-200 group-hover/card:translate-x-0.5"
        />
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useMeetingUsher, type MeetingDashboardArgs } from '@/composables/useMeetingUsher';
import DateUtility from '@/utilities/dates';

const props = defineProps<{
  events: MeetingDashboardArgs[];
}>();

const { formatDate } = DateUtility;
const meetingUsher = useMeetingUsher();

const liveMeeting = computed(() => props.events.find(event => meetingUsher.isLive(event)));
const locationInfo = computed(() => (liveMeeting.value ? meetingUsher.getLocationInfo(liveMeeting.value) : null));
</script>
